import { getIn, useField, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import { ButtonReset } from '@koob/margaret';
import { KoobInputStepperField } from '@koob/ui';
import VisuallyHidden from 'components/VisuallyHidden';
import PromotionBenefit from './PromotionBenefit';

const RANK_MIN = 1;
const RANK_MAX = 99999;

// Unranked rows go last; equal ranks keep their original order.
const byRank = (a, b) => {
  if (a.rank == null || b.rank == null) {
    return (a.rank == null) - (b.rank == null);
  }
  return a.rank - b.rank;
};

const CalculationOrderRow = ({
  name,
  title,
  subtitle,
  rankProps,
  isCurrent = false,
  onRemove,
  removeLabel,
}) => (
  <li
    className={`flex items-center gap-3 rounded-lg border border-solid px-3 py-2.5 ${
      isCurrent ? 'border-brand/30 bg-brand/5' : 'border-gray-200 bg-white'
    }`}
  >
    <KoobInputStepperField name={name} size="sm" {...rankProps} />
    <div className="flex min-w-0 flex-1 flex-col">
      <span className="truncate text-sm font-semibold text-gray-800">
        {title}
      </span>
      {subtitle && (
        <span className="truncate text-xs text-gray-500">{subtitle}</span>
      )}
    </div>
    {onRemove && (
      <ButtonReset
        type="button"
        onClick={onRemove}
        className="shrink-0 p-1 text-gray-400 transition-colors hover:text-red-500"
      >
        <VisuallyHidden>{removeLabel}</VisuallyHidden>
        <i className="fa-regular fa-trash-can" aria-hidden="true" />
      </ButtonReset>
    )}
  </li>
);

// KD-3586: ranks a promotion and each promotion it combines with; each rank
// is a calculation step on the running price, lowest first.
// - name: formik array of the combined promotions ({ id, name, calculationOrder })
// - currentRankName / currentTitle: formik field and label of the edited
//   promotion's own rank; the row is omitted without currentRankName
// - promotions: linkable promotions carrying `benefit`, shown under each name
const PromotionCalculationOrder = ({
  name,
  currentRankName,
  currentTitle,
  promotions,
  required = false,
  disabled = false,
  onRemove,
}) => {
  const { t } = useTranslation('promotionForm');
  const { values } = useFormikContext();
  const [{ value: members }] = useField(name);
  const rankProps = {
    min: RANK_MIN,
    max: RANK_MAX,
    required,
    touchRequired: required ? false : undefined,
    disabled,
  };
  const currentPromotionLabel = t('cumulativePromotions.currentPromotion');

  if (!(members?.length > 0)) {
    return null;
  }

  // Rows are displayed by rank (re-sorted on every change) while each one
  // keeps the formik path of its own member.
  const rows = [
    ...(currentRankName
      ? [
          {
            key: currentRankName,
            fieldName: currentRankName,
            rank: getIn(values, currentRankName),
            isCurrent: true,
          },
        ]
      : []),
    ...members.map((member, memberIndex) => ({
      key: member.id,
      fieldName: `${name}.${memberIndex}.calculationOrder`,
      rank: member.calculationOrder,
      member,
    })),
  ].sort(byRank);

  return (
    <div className="mt-4 flex flex-col gap-3">
      <div className="flex items-end justify-between gap-2 border-0 border-b border-solid border-gray-200 pb-3">
        <span className="text-base font-semibold text-gray-800">
          {t('cumulativePromotions.applicationPriority')}
        </span>
        <span className="text-xs text-gray-500">
          {t('cumulativePromotions.offersCount', {
            count: members.length,
          })}
        </span>
      </div>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {rows.map(({ key, fieldName, isCurrent, member }) => {
          if (isCurrent) {
            return (
              <CalculationOrderRow
                key={key}
                name={fieldName}
                title={currentTitle || currentPromotionLabel}
                subtitle={currentPromotionLabel}
                rankProps={rankProps}
                isCurrent
              />
            );
          }

          const promotion = promotions?.find(({ id }) => id === member.id);

          return (
            <CalculationOrderRow
              key={key}
              name={fieldName}
              title={member.name}
              subtitle={
                promotion?.benefit && <PromotionBenefit promotion={promotion} />
              }
              rankProps={rankProps}
              onRemove={
                disabled || !onRemove ? undefined : () => onRemove(member.id)
              }
              removeLabel={t('applicableRooms.deleteFromSelection', {
                value: member.name,
              })}
            />
          );
        })}
      </ul>
      <p className="m-0 border-0 border-l-2 border-solid border-brand pl-3 text-xs text-gray-500">
        {t('cumulativePromotions.calculationOrderHelp')}
      </p>
    </div>
  );
};

export default PromotionCalculationOrder;
